import {
    RouterModule, Routes, UrlMatchResult, UrlSegment,
} from '@angular/router';
import { NgModule } from '@angular/core';
import { Permissions } from '@bciti/types';
import { AuthGuard } from './auth/guard/auth.guard';
import { ConfigAllComponent } from './global/pages/config-all/config-all.component';
import { ExternalConsultationGuard } from './auth/guard/consultation.guard';
import { ExternalSystemsComponent } from './global/pages/external-systems/external-systems.component';
import { HomeComponent } from './global/pages/home/home.component';
import { IframePageComponent } from './global/pages/iframe/iframe.component';
import { LayoutClassicComponent } from './global/layouts/classic/classic.component';
import { LayoutFloatingCenterComponent } from './global/layouts/floating-center/floating-center.component';
import { LoginComponent } from './global/pages/login/login.component';
import { LogoutComponent } from './global/pages/logout/logout.component';
import { CallbackComponent } from './global/pages/callback/callback.component';
import { NotFoundComponent } from './global/pages/not-found/not-found.component';
import { PermissionGuard } from './user/guard/permission.guard';
import { ScopeGuard } from './user/guard/scope.guard';
import { UserCreateComponent } from './user/pages/user-create/user-create.component';
import { UserRecordsComponent } from './user/pages/user-records/user-records.component';
import { UserUpdateComponent } from './user/pages/user-update/user-update.component';
import { UserDashboardComponent } from './global/components/user/user-dashboard/user-dashboard.component';
import { UserAnalyticsComponent } from './global/components/user/user-analytics/user-analytics.component';
import { UserConfigComponent } from './global/components/user/user-config/user-config.component';
import { CollectionsListComponent } from './global/pages/collections/collections-list/collections-list.component';
import { CollectionConfigurationComponent } from './global/pages/collections/collection-configuration/collection-configuration.component';
import { ServiceStateConfigurationComponent } from './global/pages/services-state/service-state-configuration/service-state-configuration.component';
import { ServicesStateListComponent } from './global/pages/services-state/services-state-list.component';
import { ServiceStateEditComponent } from './global/pages/services-state/service-state-edit/service-state-edit.component';
import { DashboardComponent } from './global/pages/dashboard/dashboard.component';
import { ExportsComponent } from './global/pages/exports/exports.component';
import { CollectionEditComponent } from './global/pages/collections/collection-edit/collection-edit.component';
import { CollectionsDashboardComponent } from './global/components/collections/collections-dashboard/collections-dashboard.component';
import { CollectionsAnalyticsComponent } from './global/components/collections/collections-analytics/collections-analytics.component';
import { CollectionsConfigComponent } from './global/components/collections/collections-config/collections-config.component';
import { RequestListPageComponent } from './request-v2/components/request-list-page/request-list-page.component';
import { RequestDashboardComponent } from './request-v2/components/request-dashboard/request-dashboard.component';
import { RequestAnalyticsComponent } from './request-v2/components/request-analytics/request-analytics.component';
import { RequestConfigComponent } from './request-v2/components/request-config/request-config.component';
import { RequestDetailPageComponent } from './request-v2/components/request-detail-page/request-detail-page.component';
import { AccessControlListComponent } from './global/pages/access-control/access-control-list/access-control-list.component';
import { AccessControlEditComponent } from './global/pages/access-control/access-control-edit/access-control-edit.component';
import { AccessControlConfigurationComponent } from './global/pages/access-control/access-control-configuration/access-control-configuration.component';
import { RequestComponent } from './global/pages/requests/requests.component';
import { MeterDashboardComponent } from './meter/components/meter-dashboard/meter-dashboard.component';
import { MeterListComponent } from './meter/components/meter-list/meter-list.component';
import { MeterAnalyticsComponent } from './meter/components/meter-analytics/meter-analytics.component';
import { MeterConfigComponent } from './meter/components/meter-config/meter-config.component';
import { PermitDashboardComponent } from './permit/components/permit-dashboard/permit-dashboard.component';
import { PermitListPageComponent } from './permit/components/permit-list-page/permit-list-page.component';
import { PermitAnalyticsComponent } from './permit/components/permit-analytics/permit-analytics.component';
import { PermitConfigComponent } from './permit/components/permit-config/permit-config.component';
import { PermitDetailPageComponent } from './permit/components/permit-detail-page/permit-detail-page.component';

/** `/communications` and its sub-paths: the iframe opens the sub-path (`modulePath`) in the React module. */
export function communicationsMatcher(segments: UrlSegment[]): UrlMatchResult | null {
    if (segments[0]?.path !== 'communications') return null;
    const modulePath = segments
        .slice(1)
        .map((segment) => encodeURIComponent(segment.path))
        .join('/');
    return { consumed: segments, posParams: { modulePath: new UrlSegment(modulePath, {}) } };
}

const routes: Routes = [
    {
        path: 'callback',
        component: LayoutFloatingCenterComponent,
        children: [{ path: '', component: CallbackComponent }],
    },
    {
        path: '',
        component: LayoutClassicComponent,
        canActivate: [AuthGuard],
        canActivateChild: [PermissionGuard, ScopeGuard],
        children: [
            { path: '', component: HomeComponent },
            {
                path: 'user-records',
                component: UserDashboardComponent,
                data: { permissions: [Permissions.PROFILE] },
                children: [
                    { path: '', redirectTo: 'manage', pathMatch: 'full' },
                    { path: 'manage', component: UserRecordsComponent },
                    { path: 'analytics', component: UserAnalyticsComponent },
                    { path: 'config', component: UserConfigComponent },
                ],
            },
            { path: 'user-create', component: UserCreateComponent, data: { permissions: [Permissions.PROFILE] } },
            { path: 'user-update/:id', component: UserUpdateComponent, data: { permissions: [Permissions.PROFILE] } },
            { path: 'services', component: ServicesStateListComponent, data: { permissions: [Permissions.SERVICE_STATE] } },
            { path: 'services/new', component: ServiceStateConfigurationComponent, data: { permissions: [Permissions.SERVICE_STATE] } },
            { path: 'services/edit/:id', component: ServiceStateEditComponent, data: { permissions: [Permissions.SERVICE_STATE] } },
            {
                path: 'consultations',
                component: IframePageComponent,
                data: { moduleId: 'consultation-admin', permissions: [Permissions.CONSULTATION] },
                canActivate: [ExternalConsultationGuard],
            },
            {
                matcher: communicationsMatcher,
                component: IframePageComponent,
                data: { moduleId: 'communications', permissions: [Permissions.NOTICE], fullBleed: true },
            },
            {
                // The old Angular notices screens moved to Communications: keep their saved links working.
                path: 'alerts-notifications',
                children: [
                    { path: '', redirectTo: '/communications/manage', pathMatch: 'full' },
                    { path: 'manage', redirectTo: '/communications/manage', pathMatch: 'full' },
                    { path: 'create', redirectTo: '/communications/manage/new', pathMatch: 'full' },
                    { path: 'edit/:id', redirectTo: '/communications/manage/:id/edit', pathMatch: 'full' },
                    { path: 'analytics', redirectTo: '/communications/analytics', pathMatch: 'full' },
                    { path: 'config', redirectTo: '/communications/config', pathMatch: 'full' },
                    { path: 'categories', redirectTo: '/communications/config', pathMatch: 'full' },
                    { path: 'categories/create', redirectTo: '/communications/config', pathMatch: 'full' },
                    { path: 'categories/edit/:id', redirectTo: '/communications/config', pathMatch: 'full' },
                    { path: '**', redirectTo: '/communications/manage' },
                ],
            },
            {
                path: 'requests',
                component: RequestComponent,
                data: { permissions: [Permissions.REQUEST] },
            },
            {
                path: 'requests-v2',
                component: RequestDashboardComponent,
                data: { permissions: [Permissions.REQUEST] },
                children: [
                    { path: '', redirectTo: 'manage', pathMatch: 'full' },
                    {
                        path: 'manage',
                        component: RequestListPageComponent,
                        children: [{ path: 'details/:id', component: RequestDetailPageComponent }],
                    },
                    { path: 'analytics', component: RequestAnalyticsComponent },
                    { path: 'config', component: RequestConfigComponent },
                ],
            },
            {
                path: 'permits',
                component: PermitDashboardComponent,
                data: { permissions: [Permissions.PERMIT] },
                children: [
                    { path: '', redirectTo: 'manage', pathMatch: 'full' },
                    {
                        path: 'manage',
                        component: PermitListPageComponent,
                        children: [{ path: 'details/:id', component: PermitDetailPageComponent }],
                    },
                    { path: 'analytics', component: PermitAnalyticsComponent },
                    { path: 'config', component: PermitConfigComponent },
                ],
            },
            {
                path: 'parking',
                component: IframePageComponent,
                data: { moduleId: 'sdp-parking', permissions: [Permissions.PARKING] },
            },
            { path: 'voice', redirectTo: '/communications/manage', pathMatch: 'full' },
            {
                path: 'configurations',
                component: ConfigAllComponent,
                data: { permissions: [Permissions.CONFIGURATION] },
            },
            {
                path: 'external-systems',
                component: ExternalSystemsComponent,
                data: { permissions: [Permissions.EXTERNAL_SYSTEM] },
            },
            {
                path: 'parking-permit',
                component: IframePageComponent,
                data: { moduleId: 'sdp-parking-permit', permissions: [Permissions.PERMIT] },
            },
            {
                path: 'collections',
                component: CollectionsDashboardComponent,
                data: { permissions: [Permissions.COLLECTIONS] },
                children: [
                    { path: '', redirectTo: 'manage', pathMatch: 'full' },
                    { path: 'manage', component: CollectionsListComponent },
                    { path: 'analytics', component: CollectionsAnalyticsComponent },
                    { path: 'config', component: CollectionsConfigComponent },
                    { path: 'new', component: CollectionConfigurationComponent },
                    { path: 'edit/:id', component: CollectionEditComponent },
                ],
            },
            {
                path: 'dashboard',
                component: DashboardComponent,
                data: { permissions: [Permissions.DASHBOARD] },
            },
            {
                path: 'exports',
                component: ExportsComponent,
                data: { permissions: [Permissions.EXPORTS] },
            },
            {
                path: 'access-control',
                component: AccessControlListComponent,
                data: { permissions: [Permissions.ACCESS_CONTROL] },
            },
            {
                path: 'access-control/new',
                component: AccessControlConfigurationComponent,
                data: { permissions: [Permissions.ACCESS_CONTROL] },
            },
            {
                path: 'access-control/edit/:id',
                component: AccessControlEditComponent,
                data: { permissions: [Permissions.ACCESS_CONTROL] },
            },
            {
                path: 'meters',
                component: MeterDashboardComponent,
                data: { permissions: [Permissions.SMART_METER] },
                children: [
                    { path: '', redirectTo: 'manage', pathMatch: 'full' },
                    { path: 'manage', component: MeterListComponent },
                    { path: 'analytics', component: MeterAnalyticsComponent },
                    { path: 'config', component: MeterConfigComponent },
                ],
            },
        ],
    },
    {
        path: 'login',
        component: LayoutFloatingCenterComponent,
        children: [{ path: '', component: LoginComponent }],
    },
    // These routes are now handled by Zitadel — redirect to login
    { path: 'reset-password', redirectTo: '/login', pathMatch: 'full' },
    { path: 'change-password', redirectTo: '/login', pathMatch: 'full' },
    { path: 'confirm-account', redirectTo: '/login', pathMatch: 'full' },
    { path: 'logout', component: LogoutComponent },
    { path: '**', component: NotFoundComponent },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
